import { useMutation } from '@apollo/client';

import {
  type BookmarkDreamJobMutation,
  BookmarkDreamJobDocument,
  type BookmarkDreamJobMutationVariables,
  DeleteBookmarkDreamJobDocument,
  type DeleteBookmarkDreamJobMutation,
  type DeleteBookmarkDreamJobMutationVariables,
} from '@xing-com/crate-dream-machine-graphql';

import { useJobCardTracking } from '../../cards/job-card/use-job-card-tracking';
import { useSearchAndShuffleContext } from '../../context';

type UseBookmarkActionsReturn = {
  createBookmark: (deckIndex: number) => Promise<boolean>;
  deleteBookmark: (jobId: string, itemUrn: string) => Promise<boolean>;
};

export const useBookmarkActions = (): UseBookmarkActionsReturn => {
  const { trackDeleteBookmark } = useJobCardTracking();
  const { decks, setStatusBanner } = useSearchAndShuffleContext();
  const [createBookmarkMutation] = useMutation<
    BookmarkDreamJobMutation,
    BookmarkDreamJobMutationVariables
  >(BookmarkDreamJobDocument);

  const [deleteBookmarkMutation] = useMutation<
    DeleteBookmarkDreamJobMutation,
    DeleteBookmarkDreamJobMutationVariables
  >(DeleteBookmarkDreamJobDocument);

  const createBookmark = async (deckIndex: number): Promise<boolean> => {
    const card = decks[deckIndex].cards[0];
    if (!card.data.id) {
      return false;
    }

    const response = await createBookmarkMutation({
      variables: { jobId: card.data.id },
    });

    setStatusBanner({
      show: true,
      key: 'SEARCH_AND_SHUFFLE_STATUS_BANNER_BOOKMARK_ENABLED',
      type: 'success',
    });

    return response.data?.jobUpsertBookmark?.__typename === 'JobBookmark';
  };

  const deleteBookmark = async (
    jobId: string,
    itemUrn: string
  ): Promise<boolean> => {
    const response = await deleteBookmarkMutation({ variables: { jobId } });

    trackDeleteBookmark({ itemUrn });

    setStatusBanner({
      show: true,
      key: 'SEARCH_AND_SHUFFLE_STATUS_BANNER_BOOKMARK_DISABLED',
      type: 'success',
    });

    return response.data?.jobDeleteBookmark?.job?.__typename === 'VisibleJob';
  };

  return {
    createBookmark,
    deleteBookmark,
  };
};
